// Shared helpers for product / ingredient display.
// Centralised so brand+name splitting and ingredient classification
// stay consistent across the Checker, Routine, ResultCard, ProductPage, etc.

/**
 * Split a product into brand + display name without duplicating the brand.
 *
 * Many catalog rows have full_name = "CeraVe CeraVe Foaming Facial Cleanser"
 * (legacy double-prefix), or full_name = "CeraVe Foaming Facial Cleanser"
 * with brand="CeraVe". This helper returns { brand, name } so the UI can
 * render them as separate elements (smaller muted brand · larger primary
 * product name) without ever showing the brand twice.
 */
export function splitBrandAndName(product) {
  if (!product) return { brand: "", name: "" };
  const brandRaw = (product.brand || "").trim();
  // Prefer the more complete name string we have.
  const candidate = (
    product.full_name ||
    product.name ||
    product.product_name ||
    ""
  ).trim();
  if (!brandRaw) return { brand: "", name: candidate };
  if (!candidate) return { brand: brandRaw, name: "" };

  // Strip ALL leading occurrences of the brand (handles the legacy double
  // "CeraVe CeraVe …" rows). Case-insensitive, whitespace-tolerant.
  let stripped = candidate;
  // Escape regex specials in the brand name.
  const safeBrand = brandRaw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const leadingBrand = new RegExp(`^(?:${safeBrand}\\s*[-:]?\\s*)+`, "i");
  stripped = stripped.replace(leadingBrand, "").trim();
  // If stripping leaves nothing meaningful, fall back to the candidate.
  if (!stripped || stripped.length < 2) stripped = candidate;
  return { brand: brandRaw, name: stripped };
}

// --- Ingredient classification ---------------------------------------------
// Maps the diverse free-form category strings we have in MongoDB onto two
// surface buckets that consumers care about.

const ACTIVE_CATEGORIES = new Set([
  "ACTIVES",
  "Anti-acne",
  "Anti-aging peptide",
  "Antifungal / Scalp active",
  "Antioxidant",
  "Brightening",
  "EXFOLIANT",
  "Exfoliant",
  "PEPTIDE",
  "Retinoid",
  "SUNSCREEN",
  "Mineral UV filter",
  "Protection",
]);

const SUPPORTING_CATEGORIES = new Set([
  "Barrier",
  "EMOLLIENT",
  "HYDRATOR",
  "Humectant",
  "Soothing",
  "OCCLUSIVE",
]);

// Hard-coded fallbacks for ingredients whose category string is missing or
// generic ("Other") — common, so worth seeding by name.
const ACTIVE_NAMES = new Set([
  "retinol", "retinal", "retinaldehyde", "tretinoin", "adapalene",
  "niacinamide", "salicylic acid", "vitamin c", "ascorbic acid",
  "azelaic acid", "glycolic acid", "lactic acid", "mandelic acid",
  "benzoyl peroxide", "kojic acid", "alpha arbutin", "tranexamic acid",
  "bakuchiol", "hydroquinone", "resorcinol", "vitamin e", "tocopherol",
  "ferulic acid", "resveratrol", "copper peptides", "matrixyl",
  "argireline", "zinc pyrithione", "selenium sulfide", "ketoconazole",
  "aha", "bha", "pha",
]);

const SUPPORTING_NAMES = new Set([
  "ceramides", "ceramide", "ceramide np", "ceramide ap", "ceramide eop",
  "hyaluronic acid", "sodium hyaluronate", "glycerin", "panthenol",
  "squalane", "squalene", "shea butter", "centella asiatica", "cica",
  "madecassoside", "allantoin", "bisabolol", "beta glucan",
  "cholesterol", "fatty alcohols", "phytosphingosine",
]);

function lowerName(ingredient) {
  return (
    ingredient?.name ||
    ingredient?.inci_name ||
    ingredient?.id ||
    ""
  )
    .toString()
    .toLowerCase()
    .trim();
}

export function classifyIngredient(ingredient) {
  if (!ingredient) return "other";
  const cat = (ingredient.category || "").toString().trim();
  if (ACTIVE_CATEGORIES.has(cat)) return "active";
  if (SUPPORTING_CATEGORIES.has(cat)) return "supporting";
  // Fallback: name match.
  const name = lowerName(ingredient);
  if (ACTIVE_NAMES.has(name)) return "active";
  for (const a of ACTIVE_NAMES) if (a.length >= 4 && name.includes(a)) return "active";
  if (SUPPORTING_NAMES.has(name)) return "supporting";
  for (const s of SUPPORTING_NAMES) if (s.length >= 4 && name.includes(s)) return "supporting";
  return "other";
}

/**
 * Bucket a list of ingredients into { actives, supporting, other }.
 * Preserves original order within each bucket.
 */
export function bucketIngredients(list) {
  const out = { actives: [], supporting: [], other: [] };
  for (const ing of list || []) {
    const c = classifyIngredient(ing);
    if (c === "active") out.actives.push(ing);
    else if (c === "supporting") out.supporting.push(ing);
    else out.other.push(ing);
  }
  return out;
}
